// The setup check (CFO plan 5.6): the trust meter's list for the whole business — what's missing before the money
// numbers can be believed, each with where it's fixed. Shown in Setup → "Getting the numbers right" (web), at the top
// of the CFO view (web + phone) and by the MCP (ops_setup_check). Pure.
//
// Every row: { id, title, label, count, fixRoute, fixLabel, ids? }
//   title     what the check is about, said neutrally ("People without a pay basis") — the checklist line
//   label     what's wrong right now ("2 people have no pay basis, so their hours cost $0"); null when count is 0
//   count     how many (0 = fine)
//   fixRoute  where it's fixed: an in-app web path ("/team?pay=…") or a BattleBooks URL (https://…)
// Checks that need the books' numbers (opsRates/overhead, opsCfo/now — owners only) are left out until those arrive.

import { personRates, jobTypeOf, agentUids, moneyShort } from './profit.js';
import { isProjectLive } from './projectstatus.js';

const BOOKS = 'https://app.battlebooks.ca';
export const SETUP_BOOKS = {
  payroll: `${BOOKS}/settings/?tab=payroll`,
  recurring: `${BOOKS}/accounting/recurring/`,
  expenses: `${BOOKS}/expenses/`,
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const isPersonal = (p, dept) => p?.jobType === 'personal' || (dept && (dept.id === 'personal' || /^personal$/i.test(String(dept.name || ''))));

/**
 * @param {object} p
 * @param {Array}  p.members        opsMembers docs (assistant accounts — `agent: true` — are skipped)
 * @param {object} p.pay            { [uid]: opsPay doc }
 * @param {string} [p.me]           the signed-in uid (counted even when not in members)
 * @param {Array}  p.projects, p.jobs, p.departments
 * @param {object} [p.rates]        opsRates/overhead (recurring, unassignedCogs, policy) — null until the books publish
 * @param {object} [p.cfo]          opsCfo/now (budgets, forecast.undated) — null until the books publish
 * @param {Array}  [p.projectTypes], [p.taskTemplates]
 * @returns {Array<{ id, title, label, count, fixRoute, fixLabel, ids? }>}
 */
export function setupCheck({ members = [], pay = {}, me = null, projects = [], jobs = [], departments = [], rates = null, cfo = null, projectTypes = [], taskTemplates = [] } = {}) {
  const rows = [];
  const row = (id, title, count, label, fixRoute, fixLabel, ids) => rows.push({ id, title, label: count > 0 ? label : null, count, fixRoute, fixLabel, ...(ids ? { ids } : {}) });

  // people without a pay basis (assistants never count as hours, so they need none)
  const agents = agentUids(members);
  const uids = [...new Set([...(members || []).filter((m) => m && m.active !== false).map((m) => m.uid || m.id), ...(me ? [me] : [])])].filter((u) => u && !agents.has(u));
  const noPay = uids.filter((u) => personRates(pay?.[u] || null).missing);
  row('pay', 'People without a pay basis', noPay.length,
    noPay.length === 1 && noPay[0] === me ? 'You have no pay basis, so your hours cost $0' : `${plural(noPay.length, 'person has', 'people have')} no pay basis, so their hours cost $0`,
    noPay.length === 1 ? `/team?pay=${encodeURIComponent(noPay[0] === me ? 'me' : noPay[0])}` : '/team', 'Team → Pay', noPay);

  // open projects without a job type (personal ones need none)
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const deptById = new Map(departments.map((d) => [d.id, d]));
  const untyped = projects.filter((p) => p?.id && !p.deletedAt && isProjectLive(p) && !isPersonal(p, deptById.get(p.departmentId))
    && !jobTypeOf(p, p.bookProjectId ? jobById.get(p.bookProjectId) || null : null, deptById.get(p.departmentId) || null)).map((p) => p.id);
  row('type', 'Projects without a type', untyped.length, `${plural(untyped.length, 'open project has', 'open projects have')} no job type, so what the work earns can’t be told`,
    untyped.length === 1 ? `/projects/${untyped[0]}/cost` : '/projects', untyped.length === 1 ? 'Project → Cost' : 'Projects', untyped);

  if (rates) {
    const rec = rates.recurring || {};
    row('recurring', 'Recurring costs', rec.rules ? 0 : 1, 'No recurring costs set up (insurance, truck, phone, software, rent), so overhead reads low', SETUP_BOOKS.recurring, 'BattleBooks → Recurring');
    const loose = Number(rates.unassignedCogs) || 0;
    row('receipts', 'Receipts not tied to a job or overhead', loose > 0 ? 1 : 0, `${moneyShort(loose)} of job costs aren’t tied to a job or overhead`, SETUP_BOOKS.expenses, 'BattleBooks → Expenses');
  }

  row('templates', 'Templates', projectTypes.length || taskTemplates.length ? 0 : 1, 'No project or task templates yet — a starter pack sets some up', '/settings/templates', 'Setup → Templates');

  if (cfo) {
    const budgets = Array.isArray(cfo.budgets) ? cfo.budgets.filter((b) => b && b.active !== false) : [];
    row('budgets', 'Budgets', budgets.length ? 0 : 1, 'No budgets set, so nothing warns before a line runs over', '/cfo#cfo-budgets', 'CFO → Budgets');
    const undated = cfo.forecast?.undated || [];
    row('stages', 'Billing stages without dates', undated.length, `${plural(undated.length, 'billing stage has', 'billing stages have')} no date, so the cash forecast leaves them out`, '/cfo#cfo-forecast', 'CFO → Forecast');
  }

  if (rates) {
    const pol = rates.policy || {};
    const missing = [pol.targetMarginPct == null ? 'target margin' : null, pol.minimumWage == null ? 'minimum wage' : null].filter(Boolean);
    row('policy', 'Target margin and minimum wage', missing.length, `No ${missing.join(' or ')} in BattleBooks payroll settings`, SETUP_BOOKS.payroll, 'BattleBooks → Payroll settings');
    // `wcbRegistered` is the saved answer (null = never set); books published before it read off wcbCounted
    const wcbUnset = 'wcbRegistered' in pol ? pol.wcbRegistered == null : pol.wcbCounted !== true;
    row('wcb', 'WCB status', wcbUnset ? 1 : 0, 'WCB status not set (registered or not), so employer costs may read short', SETUP_BOOKS.payroll, 'BattleBooks → Payroll settings');
  }
  return rows;
}

/** Only the rows that need something. */
export const setupTodo = (rows = []) => rows.filter((r) => r.count > 0);

// Setup rows the CFO view's own trust strip already says (cfo.js / trust.js keys) — not said twice there.
const SAID_BY = { recurring: 'oh:recurring', receipts: 'oh:nojob', stages: 'cfo:undated' };

/**
 * The rows as trust-strip items (the CFO view's slot): { key, text, fix: 'setup', route }. `trust` = the strip's own
 * items; a row it already covers is left out.
 */
export function setupTrustItems(rows = [], trust = []) {
  const have = new Set((trust || []).map((t) => t.key));
  return setupTodo(rows).filter((r) => !have.has(SAID_BY[r.id])).map((r) => ({ key: `setup:${r.id}`, text: r.label, fix: 'setup', route: r.fixRoute, setupId: r.id }));
}
